import { useState, useEffect } from "react";
import { GetData } from "../../utilities/GetData";
import {
  Input,
  Card,
  CardHeader,
  CardContent,
  Button,
  FormControl,
  MenuItem,
  InputLabel,
  Select,
  LinearProgress,
} from "@material-ui/core";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import { makeStyles } from "@material-ui/core/styles";
import useDialog from "../../hooks/useDialog";
import axios from "axios";

const useStyles = makeStyles((theme) => ({
  card: {
    [theme.breakpoints.between("md", "lg")]: {
      width: "90%",
    },
  },
  badgeApproved: {
    color: "white",
    backgroundColor: "#4E9F3D",
  },
  badgeReview: {
    color: "white",
    backgroundColor: "#C8C6C6",
  },
  filter: {
    marginTop: "25px",
    display: "flex",
    alignItems: "center",
    flexDirection: "row",
    padding: "15px 5px",
    [theme.breakpoints.between("md", "lg")]: {
      padding: "10px 0px",
    },
    border: "3px dotted #f3f3f3",
  },
  filterSelector: {
    minWidth: 120,
    marginRight: 20,
  },
  dateSelector: {
    marginRight: 20,
  },
  field: {
    margin: "0 30px 0 0",
  },
}));

// Reusable Report Card Component
const ReportCard = ({ header, children }) => {
  const classes = useStyles();
  return (
    <Card className={classes.card} style={{ margin: "2rem 0" }}>
      <CardHeader title={header} style={{ borderBottom: "1px solid #f3f3f3" }} />
      <CardContent>{children}</CardContent>
    </Card>
  );
};

const Report = () => {
  const classes = useStyles();
  const { showSnackbar } = useDialog();
  const [isBank, setIsBank] = useState(false);
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [banks, setBanks] = useState([]);
  const [approvalStatuses] = useState([
    { title: "ყველა", value: "all" },
    { title: "განიხილება", value: "review" },
    { title: "დამტკიცებული", value: "approved" },
    { title: "უარყოფილი", value: "rejected" },
    { title: "გაუქმებული", value: "suspended" },
  ]);
  const [components, setComponents] = useState([
    { title: "მონიტორინგი", value: "monitoring", agency_only: true },
    { title: "მონიტორინგი (მიკრო)", value: "monitoring_cgs", agency_only: true },
  ]);
  const currentYear = new Date().getFullYear();
  const defaultStartDate = new Date(currentYear, 0, 1);
  const defaultEndDate = new Date(currentYear, 11, 31);

  const [filterData, setFilterData] = useState({
    component: "all",
    status: "all",
    all_schedules: false,
    refinancing_date_from: defaultStartDate.toISOString().split("T")[0],
    refinancing_date_to: defaultEndDate.toISOString().split("T")[0],
    scheduling_date_from: defaultStartDate.toISOString().split("T")[0],
    scheduling_date_to: defaultEndDate.toISOString().split("T")[0],
    bank_id: "",
    loan_id: "",
  });

  // For the slider
  const MIN_YEAR = 2020;
  const MAX_YEAR = currentYear + 1;
  const [dateRangeValue, setDateRangeValue] = useState([currentYear, currentYear]);
  const [schedulingDateRangeValue, setSchedulingDateRangeValue] = useState([currentYear, currentYear]);
  const [reportLoading, setReportLoading] = useState(false);
  const [paymentDataLoading, setPaymentDataLoading] = useState(false);
  const [generateMismatchSheet, setGenerateMismatchSheet] = useState(false);

  const handlePaymentUploadSuccess = (data) => {
    setPaymentDataLoading(false);
    if (data.message) {
      showSnackbar(data.message, "success");
    } else {
      showSnackbar("გადახდების ფაილი წარმატებით აიტვირთა", "success");
    }
  };

  const handlePaymentUploadError = (error) => {
    setPaymentDataLoading(false);
    console.error("Error uploading payment file:", error);
    if (error.response && error.response.data && error.response.data.message) {
      showSnackbar(error.response.data.message, "error");
    } else {
      showSnackbar("გადახდების ფაილის ატვირთვა ვერ მოხერხდა", "error");
    }
  };

  useEffect(() => {
    const role = localStorage.getItem("role");
    const permissions = JSON.parse(localStorage.getItem("permissions") || "[]");

    if (role === "ROLE_BANK") setIsBank(true);
    if (role === "ROLE_MONITORING") {
      setIsMonitoring(true);
      setComponents((prev) => prev.filter((c) => c.value === "monitoring"));
    }
    if (role === "ROLE_MONITORING_CGS") {
      setIsMonitoring(true);
      setComponents((prev) => prev.filter((c) => c.value === "monitoring_cgs"));
    }
    if (role === "ROLE_MONITORING_REVIEW") {
      setIsMonitoring(true);
      if (permissions.includes("REVIEW_MONITORING_CGS")) {
        setComponents((prev) => prev.filter((c) => c.value === "monitoring_cgs"));
      } else if (permissions.includes("REVIEW_MONITORING")) {
        setComponents((prev) => prev.filter((c) => c.value === "monitoring"));
      }
    }
    GetData("general_info")
      .then((resp) => {
        const allBanks = [{ name: "ყველა", _id: undefined }, ...resp?.banks];
        setBanks(allBanks);
        setFilterData((prev) => ({ ...prev, bank_id: "" }));
      })
      .catch(() => {});
  }, []);

  // Generic function to download report
  const downloadReport = (endpoint, fileName, isRefinancingReport = false) => {
    setReportLoading(true);
    const dataPayload = {
      ...filterData,
      ...(isRefinancingReport && { generate_mismatch_sheet: generateMismatchSheet }),
    };
    axios({
      method: "post",
      url: `${process.env.REACT_APP_BASE_URL}${endpoint}`,
      data: dataPayload,
      responseType: "blob",
    })
      .then((response) => {
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        setReportLoading(false);
      })
      .catch((error) => {
        const reader = new FileReader();
        reader.readAsText(error.response.data);
        reader.onload = () => {
          try {
            const res = JSON.parse(reader.result);
            showSnackbar(res.message || "დაფიქსირდა შეცდომა", "error");
          } catch {
            showSnackbar("დაფიქსირდა შეცდომა", "error");
          }
        };
        setReportLoading(false);
      });
  };

  const generateReport = () => downloadReport("generate_report", "ApplicationsReport.xlsx");

  // Reusable filter section component
  const renderFilters = () => (
    <div className={classes.filter}>
      {!isBank && (
        <FormControl className={classes.filterSelector}>
          <InputLabel id="bank-label" shrink>
            ბანკი
          </InputLabel>
          <Select
            labelId="bank-label"
            id="bank-select"
            value={filterData.bank_id}
            onChange={(e) => setFilterData({ ...filterData, bank_id: e.target.value })}
            displayEmpty
            renderValue={(value) => {
              if (value === "" || value === undefined) return "ყველა";
              return banks?.find((bank) => String(bank._id) === String(value))?.name || "ყველა";
            }}
          >
            {banks?.map((bank, index) => (
              <MenuItem key={index} value={bank._id}>
                {bank.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      <FormControl className={classes.filterSelector} style={{ display: isMonitoring ? "none" : "block" }}>
        <InputLabel id="status-label">სტატუსი</InputLabel>
        <Select labelId="status-label" id="status-select" value={filterData.status} onChange={(e) => setFilterData({ ...filterData, status: e.target.value })}>
          {approvalStatuses.map((status, idx) => (
            <MenuItem key={idx} value={status.value}>
              {status.title}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl className={classes.dateSelector}>
        <InputLabel shrink>თარიღი (დან)</InputLabel>
        <Input type="date" onChange={(e) => setFilterData({ ...filterData, date_from: e.target.value })} />
      </FormControl>
      <FormControl className={classes.dateSelector}>
        <InputLabel shrink>თარიღი (მდე)</InputLabel>
        <Input type="date" onChange={(e) => setFilterData({ ...filterData, date_to: e.target.value })} />
      </FormControl>
      <FormControl className={classes.filterSelector}>
        <InputLabel id="component-label" shrink>
          კომპონენტი
        </InputLabel>
        <Select
          labelId="component-label"
          id="component-select"
          value={filterData.component}
          onChange={(e) => setFilterData({ ...filterData, component: e.target.value })}
        >
          {components.map((comp) => {
            if (comp.agency_only && isBank) return null;
            return (
              <MenuItem key={comp.value} value={comp.value}>
                {comp.title}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    </div>
  );

  return (
    <>
      <Card className={classes.card}>
        <CardHeader title="რაპორტის გენერირება" style={{ borderBottom: "1px solid #f3f3f3" }} />
        <CardContent>
          {renderFilters()}
          <div style={{ display: "flex", alignItems: "center", marginTop: 20 }}>
            <Button
              style={{ marginRight: 30 }}
              disabled={reportLoading}
              color="primary"
              variant="contained"
              startIcon={<CloudDownloadIcon />}
              onClick={generateReport}
            >
              ჩამოტვირთვა
            </Button>
          </div>
        </CardContent>
        <LinearProgress style={{ margin: "0 auto", display: reportLoading ? "block" : "none" }} />
      </Card>
    </>
  );
};

export default Report;
